import { Collapsible, ConfigContext } from '@velinsi/smp-widgets-ui';
import { FC, useContext } from 'react';
import { PLAYER_ENTITY } from '../../../../../packages/constants/entityTypes';
import { GenericLayout } from '../../../../../packages/constants/generic';
import { IFootballLineupsConfig, IMatch, IMatchPlayerLineup, ISubscriberInfo } from '../../../../../packages/types';
import { getTeamColor, getLabelValue, toSnakeCase } from '@sportal365/smp-widget-utils';
import LineupsRow, { ParticipantProps } from './LineupsRow';

interface LineupCollapsibleProps {
  title: string;
  initialState?: 'open' | 'closed';
  verticalMode?: boolean;
  homeTeamPlayers: Partial<IMatchPlayerLineup>[];
  awayTeamPlayers: Partial<IMatchPlayerLineup>[];
  footballSingleEvent: IMatch;
  handleClick: (value: ISubscriberInfo, clickType: string) => void;
  selectedTabMobile: number;
  mode: GenericLayout;
}

const LineupCollapsible: FC<LineupCollapsibleProps> = ({
  title,
  initialState = 'closed',
  verticalMode,
  homeTeamPlayers,
  awayTeamPlayers,
  footballSingleEvent,
  handleClick,
  selectedTabMobile,
  mode,
}) => {
  const config = useContext(ConfigContext) as IFootballLineupsConfig;
  const assertParticipantVisibility = (index: number, participant: ParticipantProps) =>
    mode === GenericLayout.HORIZONTAL || selectedTabMobile === index ? participant : undefined;

  if (homeTeamPlayers.length === 0 && awayTeamPlayers.length === 0) {
    return null;
  }

  const displayTitle = getLabelValue(config?.dataLabels?.[toSnakeCase(title)], title);

  return (
    <Collapsible text={displayTitle} initialState={initialState} verticalMode={verticalMode}>
      {Array(Math.max(homeTeamPlayers.length, awayTeamPlayers.length))
        .fill(null)
        .map((_, index) => {
          const homeTeamPlayerLineup = homeTeamPlayers[index];
          const participant1 = homeTeamPlayerLineup
            ? assertParticipantVisibility(0, {
                data: homeTeamPlayerLineup.player,
                shirtNumber: homeTeamPlayerLineup.shirtNumber,
                events: homeTeamPlayerLineup.events,
                entityType: PLAYER_ENTITY,
                shirtColor: getTeamColor(config?.dataHomeTeamColor, footballSingleEvent.homeTeam.shirtColor),
                handleClick: handleClick,
              })
            : undefined;
          const awayTeamPlayerLineup = awayTeamPlayers[index];
          const participant2 = awayTeamPlayerLineup
            ? assertParticipantVisibility(1, {
                data: awayTeamPlayerLineup.player,
                shirtNumber: awayTeamPlayerLineup.shirtNumber,
                events: awayTeamPlayerLineup.events,
                entityType: PLAYER_ENTITY,
                shirtColor: getTeamColor(config?.dataAwayTeamColor, footballSingleEvent.awayTeam.shirtColor, false),
                handleClick: handleClick,
              })
            : undefined;

          if (!participant1 && !participant2) {
            return null;
          }

          return <LineupsRow key={index} isEvenRow={index % 2 === 0} participants={[participant1, participant2]} />;
        })}
    </Collapsible>
  );
};

export default LineupCollapsible;
